/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Button,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Icon,
  Text,
  Flex,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { InfoOIcon } from '@tonic-one/react-icons';
import { useEffect, useRef, useState } from 'react';
import { AuthenticationInfoRes } from '@Types/provision';
import { ProvisionApi } from '@Apis';
import i18next from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { ProvisionOrgInfo, StepIcon } from '../ProvisionModal/share';
import ErrorAlert from '@Components/Common/ErrorAlert';
import { reportPendo } from '../../../utils/setupPendo';
import { AxiosResponse } from 'axios';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import CSpinner from '@Components/CSpinner';
import { ServiceForReAuth } from '@Types/administrator';

const CommonReAuthModal = ({
  orgId,
  service,
  authAccount,
  onClose: onCloseProp,
  refreshData,
}: {
  orgId: string;
  service: ServiceForReAuth;
  authAccount: string;
  onClose: () => void;
  refreshData: () => void;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['provision', 'common']);
  const [reAuthDone, setReAuthDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const timeRef = useRef<any>(null);

  const onClose = () => {
    onCloseProp();
    if (reAuthDone) refreshData();
  };

  const _service = service as
    | ServiceForReAuth.Exchange
    | ServiceForReAuth.OneDrive
    | ServiceForReAuth.SharePoint
    | ServiceForReAuth.Teams
    | ServiceForReAuth.TeamsChat
    | ServiceForReAuth.Box
    | ServiceForReAuth.Mip
    | ServiceForReAuth.Dropbox
    | ServiceForReAuth.GoogleDrive;
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);

  const cookieName = {
    [ServiceForReAuth.Exchange]: 'provision_status_azuread_graph_ex',
    [ServiceForReAuth.OneDrive]: 'provision_status_azuread_graph_od',
    [ServiceForReAuth.SharePoint]: 'provision_status_azuread_graph_sp',
    [ServiceForReAuth.Teams]: 'provision_status_azuread_graph_teams',
    [ServiceForReAuth.Box]: 'provision_status_box',
    [ServiceForReAuth.TeamsChat]: 'provision_status_azuread_graph_teams_chat',
    [ServiceForReAuth.Mip]: 'provision_status_mip_check_license',
    [ServiceForReAuth.Dropbox]: 'provision_status_dropbox',
    [ServiceForReAuth.GoogleDrive]: 'provision_status_googledrive',
  }[_service];

  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }

    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service,
    });
    setErrorMsg(undefined);
    setLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const provisionInfo = getProvisionCookie(cookieName);
      if (provisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code, state } = JSON.parse(provisionInfo);
        if (error === undefined || error.length === 0) {
          let res: AxiosResponse<any, any>;
          try {
            if (
              service === ServiceForReAuth.Box ||
              service === ServiceForReAuth.Dropbox ||
              service === ServiceForReAuth.GoogleDrive
            ) {
              res = await ProvisionApi.apiSaveReAuthInfoForStorage(
                service,
                {
                  authCode: code,
                  csrfCode: state,
                },
                authAccount,
                orgId
              );
            } else {
              res = await ProvisionApi.apiSaveReAuthInfo(
                service,
                code,
                authAccount,
                orgId
              );
            }
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.${service}.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setReAuthDone(true);
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t(`provision.service.${service}.error.deniedGrant`));
        }
        setLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  useEffect(() => {
    ProvisionApi.apiGetReAuthAuthenticationInfo(_service, orgId)
      .then((res) => {
        setAuthenticationInfo(res.data);
      })
      .catch((e) => {
        console.error(e);
        setAuthenticationInfo(null);
      });
  }, []);

  if (!authenticationInfo) {
    return <></>;
  }

  return (
    <>
      {loading && <CSpinner />}
      <Modal isOpen={true} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(`provision.reAuth.service.${service}.title`)}
          </ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />
            {/* teams chat info */}
            {_service === ServiceForReAuth.TeamsChat && (
              <Alert
                border="0px"
                severity="info"
                variant="outline"
                px="0"
                pt="0"
              >
                <Text
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight="400"
                  color={colorStyle.color.secondary}
                >
                  <Trans
                    i18nKey="provision.service.teamsChat.note"
                    ns="provision"
                  >
                    placeholder
                    <Link
                      href={getCasOLHLink(
                        CAS_DOC_LINK.provision.teamsChat.note,
                        i18next.language
                      )}
                      target="_blank"
                    >
                      placeholder
                    </Link>
                    placeholder
                  </Trans>
                </Text>
              </Alert>
            )}
            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <Flex gap="20px" my="10px">
              <StepIcon checked={reAuthDone} />
              <Flex direction="column" gap="12px">
                <Text>
                  {t(`provision.reAuth.service.${service}.grantPermission`)}
                </Text>
                {reAuthDone ? (
                  <Text color={colorStyle.color.tertiary}>
                    {t('provision.common.permissionGranted')}
                  </Text>
                ) : (
                  <div>
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      onClick={onClick}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  </div>
                )}
              </Flex>
            </Flex>

            {/* Box note */}
            {[
              ServiceForReAuth.Box,
              ServiceForReAuth.Dropbox,
              ServiceForReAuth.GoogleDrive,
            ].includes(_service) && (
              <Flex gap="8px" pt="16px">
                <Icon as={InfoOIcon} color={colorStyle.color.info} />
                <Text fontSize="semibold">{t('common.label.note')}:</Text>
                <Text>{t(`provision.reAuth.service.${_service}.note`)}</Text>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CommonReAuthModal;
